import React, { useState, useRef, useEffect } from "react";

/**
 * A reusable searchable select component with filtering capability.
 * Supports grouped options (filtered vs unfiltered) for showing relevant options first.
 */
const SearchableSelect = ({
  value,
  onChange,
  options = [],
  unfilteredOptions = [],
  placeholder = "Select...",
  allOptionLabel = "All",
  label,
  displayFormatter,
  className = "",
  posthogId,
  posthogAttribute,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const containerRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
        setSearchTerm("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const getDisplayValue = (optionValue) => {
    if (displayFormatter) {
      return displayFormatter(optionValue);
    }
    return optionValue;
  };

  const filterOptions = (optionsList) => {
    if (!searchTerm) return optionsList;
    const lowerSearchTerm = searchTerm.toLowerCase();
    return optionsList.filter((option) => {
      const displayValue = getDisplayValue(option);
      return displayValue.toLowerCase().includes(lowerSearchTerm);
    });
  };

  const filteredPrimaryOptions = filterOptions(options);
  const filteredSecondaryOptions = filterOptions(unfilteredOptions);
  const hasOptions = filteredPrimaryOptions.length > 0 || filteredSecondaryOptions.length > 0;

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    setSearchTerm("");
  };

  const handleInputChange = (e) => {
    setSearchTerm(e.target.value);
    if (!isOpen) {
      setIsOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsOpen(true);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    handleSelect("");
  };

  const currentDisplayValue = value ? getDisplayValue(value) : "";

  return (
    <div className={`relative ${className}`} ref={containerRef}>
      {label && (
        <label className="block text-sm font-medium text-gray-700 mb-1">
          {label}
        </label>
      )}
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className="w-full p-2 border border-gray-300 rounded pr-8"
          placeholder={value ? currentDisplayValue : placeholder}
          value={isOpen ? searchTerm : (value ? currentDisplayValue : "")}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          data-posthog={posthogId}
          {...(posthogAttribute && value ? { [posthogAttribute]: value } : {})}
        />
        {value && (
          <button
            type="button"
            className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
            onClick={handleClear}
            title="Clear selection"
          >
            ✕
          </button>
        )}
      </div>
      
      {isOpen && (
        <div className="absolute z-50 w-full mt-1 bg-white border border-gray-300 rounded shadow-lg max-h-60 overflow-y-auto">
          {!searchTerm && (
            <div
              className="p-2 hover:bg-blue-100 cursor-pointer text-gray-500"
              onClick={() => handleSelect("")}
            >
              {allOptionLabel}
            </div>
          )}
          
          {filteredPrimaryOptions.length > 0 && (
            <>
              {filteredPrimaryOptions.map((option) => (
                <div
                  key={option}
                  className={`p-2 hover:bg-blue-100 cursor-pointer ${
                    option === value ? "bg-blue-50 font-medium" : ""
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {getDisplayValue(option)}
                </div>
              ))}
            </>
          )}
          
          {filteredSecondaryOptions.length > 0 && (
            <>
              {filteredPrimaryOptions.length > 0 && (
                <div className="border-t border-gray-200"></div>
              )}
              {filteredSecondaryOptions.map((option) => (
                <div
                  key={option}
                  className={`p-2 hover:bg-blue-100 cursor-pointer text-gray-500 ${
                    option === value ? "bg-blue-50 font-medium" : ""
                  }`}
                  onClick={() => handleSelect(option)}
                >
                  {getDisplayValue(option)}
                </div>
              ))}
            </>
          )}
          
          {!hasOptions && searchTerm && (
            <div className="p-2 text-gray-500 italic">
              No matches found
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SearchableSelect;
